import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  Package,
  MapPin,
  Weight,
  Ruler,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Ship,
  ArrowLeft,
  ChevronUp,
  FileX,
  Search,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Loader } from '../../../components/ui/Loader';
import { Spinner } from '../../../components/ui/Spinner';
import { rateService } from '../../../services/rate';
import { WarehouseRow } from '../../../components/WarehouseRow';
import {
  ordersService,
  getShipmentIdsFromPickupResponse,
} from '../../../services/orders';
import { bulkService } from '../../../services/bulk';
import { ValidatedBulkOrder } from '../../../utils/bulkUpload';
import { CourierRate, ServiceRate } from '../add-order/rateSelection';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useAuth } from '../../../hooks/useAuth';
import { useAddress } from '../../../hooks/useAddress';
import { Select } from '../../../components/ui/Select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuItem,
} from '../../../components/ui/DropdownMenu';
import { cn } from '../../../utils/cn';
import Tooltip from '../../../components/ui/Tooltip';
import { Checkbox } from '../../../components/ui/Checkbox';

interface BulkProcessModalProps {
  orders: ValidatedBulkOrder[];
  warehouse?: any;
  addressData?: any;
  file?: File;
  onClose: () => void;
}

interface OrderProcessState {
  rates: CourierRate[];
  isLoadingRates: boolean;
  rateError: string | null;
  selectedRate: {
    courierId: number;
    courierName: string;
    serviceId: number;
    serviceName: string;
    totalCharge: number;
    tat: number;
    planId: number;
  } | null;
  status: 'idle' | 'loading_rates' | 'ready' | 'shipping' | 'success' | 'error';
  shipmentId?: number;
}

export function BulkProcessModal({
  orders: initialOrders,
  warehouse,
  addressData: passedAddressData,
  file,
  onClose,
}: BulkProcessModalProps) {
  const { user } = useAuth();
  const { data: addressQueryData } = useAddress(user?.id as string);
  const addressData = passedAddressData || addressQueryData;
  const navigate = useNavigate();

  const [orders, setOrders] = useState<ValidatedBulkOrder[]>(initialOrders);
  const [processStates, setProcessStates] = useState<
    Record<string, OrderProcessState>
  >({});
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [currentErrorIdx, setCurrentErrorIdx] = useState<number>(-1);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const fetchRatesForOrder = React.useCallback(
    async (order: ValidatedBulkOrder) => {
      // Get the target warehouse from the parameter or fallback to the current warehouse
      const targetWarehouse = (order as any).origin_address;
      if (!targetWarehouse || !user?.id) {
        console.warn('Skipping rate fetch: missing warehouse or user id');
        return;
      }

      try {
        const rateData = {
          pickup_pincode: String(targetWarehouse.pincode),
          delivery_pincode: order.pincode,
          invoice_value: order.invoiceValue,
          payment_mode:
            order.paymentMode === 'COD'
              ? ('COD' as const)
              : ('online' as const),
          product_type: 'Parcel' as const,
          delivery_mode: 'Both' as const,
          insurance: false,
          number_of_package: 1,
          package_details: [
            {
              package_content: order.productName,
              no_of_items: order.quantity,
              invoice_value: order.invoiceValue,
              package_dimension: {
                weight: Number(order.weight),
                length: Number(order.length),
                width: Number(order.width),
                height: Number(order.height),
              },
            },
          ],
        };

        const apiResponse: any = await rateService.calculateRates(
          user.id,
          rateData
        );

        // DEBUG: Log API response for debugging
        console.log('=== RATE API RESPONSE ===');
        console.log('apiResponse:', JSON.stringify(apiResponse, null, 2));
        console.log('apiResponse.couriers:', apiResponse?.couriers);
        console.log('typeof apiResponse:', typeof apiResponse);
        console.log('Array.isArray(apiResponse):', Array.isArray(apiResponse));
        console.log('================================');

        const formattedRates: CourierRate[] = (apiResponse?.couriers || []).map(
          (courier: any) => ({
            courierId: courier.courier_id,
            courierName: courier.name,
            logoUrl: courier.logo_url,
            services: (courier.service_types || []).map((service: any) => ({
              serviceId: service.service_id,
              serviceName: service.service_name,
              displayName: service.display_name,
              tat: service.expected_delivery_days,
              weight: service.weight || apiResponse.display_weight || 0,
              volumetricWeight: service.volumetric_weight || 0,
              totalCharge: service.rate_breakup?.total_charge || 0,
              baseWeightSlab: service.rate_breakup?.base_weight_slab || 0,
              planId: service.plan_id,
            })),
          })
        );

        let defaultRate: OrderProcessState['selectedRate'] = null;
        if (formattedRates.length > 0) {
          const allServices = formattedRates.flatMap((c: CourierRate) =>
            c.services.map((s: any) => ({
              ...s,
              courierId: c.courierId,
              courierName: c.courierName,
            }))
          );
          if (allServices.length > 0) {
            const cheapest = allServices.reduce((prev, curr) =>
              prev.totalCharge < curr.totalCharge ? prev : curr
            );
            defaultRate = {
              courierId: cheapest.courierId,
              courierName: cheapest.courierName,
              serviceId: cheapest.serviceId,
              serviceName: cheapest.serviceName,
              totalCharge: cheapest.totalCharge,
              tat: cheapest.tat,
              planId: cheapest.planId,
            };
          }
        }

        setProcessStates((prev) => ({
          ...prev,
          [order.internalId]: {
            ...prev[order.internalId],
            rates: formattedRates,
            isLoadingRates: false,
            selectedRate: defaultRate,
            status: defaultRate ? 'ready' : 'error',
            rateError:
              formattedRates.length === 0
                ? 'No couriers available for this route'
                : null,
          },
        }));
      } catch (err: any) {
        setProcessStates((prev) => ({
          ...prev,
          [order.internalId]: {
            ...prev[order.internalId],
            isLoadingRates: false,
            rateError: err.message || 'Failed to fetch rates',
            status: 'error',
          },
        }));
      }
    },
    [user?.id]
  );

  // Initialize states
  useEffect(() => {
    if (!orders.length) return;

    setProcessStates((prev) => {
      const newStates = { ...prev };
      let hasChanges = false;
      orders.forEach((order) => {
        if (!newStates[order.internalId]) {
          newStates[order.internalId] = {
            rates: [],
            isLoadingRates: order.isValid,
            rateError: order.isValid
              ? null
              : 'Validation failed. Please check details.',
            selectedRate: null,
            status: order.isValid ? 'loading_rates' : 'error',
          };
          hasChanges = true;
        }
      });
      return hasChanges ? newStates : prev;
    });
  }, [orders]);

  // Trigger rate fetch when warehouse/states available
  const fetchedOrdersRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!orders.length || !user?.id) return;

    setProcessStates((prev) => {
      const newStates = { ...prev };
      let hasChanges = false;
      
      orders.forEach((order) => {
        if (order.isValid && !fetchedOrdersRef.current.has(order.internalId)) {
          newStates[order.internalId] = {
            ...newStates[order.internalId],
            isLoadingRates: true,
            status: 'loading_rates',
            rates: [],
          };
          hasChanges = true;
        }
      });
      return hasChanges ? newStates : prev;
    });

    orders.forEach((order) => {
      if (order.isValid && !fetchedOrdersRef.current.has(order.internalId)) {
        fetchedOrdersRef.current.add(order.internalId);
        fetchRatesForOrder(order);
      }
    });
  }, [orders, fetchRatesForOrder, user?.id]);

  const handleRateSelect = (
    internalId: string,
    courier: CourierRate,
    service: ServiceRate
  ) => {
    setProcessStates((prev) => ({
      ...prev,
      [internalId]: {
        ...prev[internalId],
        selectedRate: {
          courierId: courier.courierId,
          courierName: courier.courierName,
          serviceId: service.serviceId,
          serviceName: service.serviceName,
          totalCharge: service.totalCharge,
          tat: service.tat,
          planId: (service as any).planId || 1,
        },
        status: 'ready',
      },
    }));
  };

  const handleDeleteRow = (idx: number) => {
    setOrders((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIndices((prev) =>
      prev.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))
    );
    toast.success('Order removed');
  };

  const deleteSelected = () => {
    if (selectedIndices.length === 0) return;
    const newData = orders.filter((_, idx) => !selectedIndices.includes(idx));
    setOrders(newData);
    setSelectedIndices([]);
    toast.success(`Removed ${selectedIndices.length} orders`);
  };

  const jumpToErrorRow = (direction: 'next' | 'prev') => {
    const errorIndices = orders
      .map((o, i) =>
        !o.isValid || processStates[o.internalId]?.status === 'error' ? i : -1
      )
      .filter((i) => i !== -1);

    if (errorIndices.length === 0) return;

    let nextIdx;
    if (direction === 'next') {
      const idx = errorIndices.find((i) => i > currentErrorIdx);
      nextIdx = idx !== undefined ? idx : errorIndices[0];
    } else {
      const reversed = [...errorIndices].reverse();
      const idx = reversed.find((i) => i < currentErrorIdx);
      nextIdx = idx !== undefined ? idx : reversed[0];
    }

    setCurrentErrorIdx(nextIdx);
    rowRefs.current[nextIdx]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleShipNow = async () => {
    const readyIndices = orders
      .map((_, idx) => idx)
      .filter((idx) => processStates[orders[idx].internalId]?.status === 'ready');

    if (readyIndices.length === 0) {
      toast.error('No ready orders to ship. Please assign couriers first.');
      return;
    }

    if (!file) {
      toast.error('Original CSV file is missing. Please upload the file again.');
      return;
    }

    setIsFinalizing(true);
    const toastId = toast.loading(`Uploading and processing ${readyIndices.length} orders...`);

    try {
      // Build warehouse DTO mapping from currentWarehouse
      // Generate CSV Blob from valid orders
      const headers = [
        'Order ID', 'Receiver Name', 'Receiver Phone', 'Receiver Email',
        'Address', 'Address 2', 'Landmark', 'Pincode', 'City', 'State',
        'Weight (kg)', 'Length (cm)', 'Width (cm)', 'Height (cm)',
        'Product Name', 'Quantity', 'Invoice Value', 'SKU', 'HSN',
        'Payment Mode', 'Pickup Date', 'Alt Phone',
        'Address Nickname', 'Pickup Contact Name',
        'Pickup Phone', 'Pickup Address', 'Pickup Address 2', 'Pickup Landmark',
        'Pickup Pincode', 'Pickup City', 'Pickup State'
      ];
      
      const csvRows = [headers.join(',')];
      
      orders.forEach((order, idx) => {
        const row = [
          order.orderId, order.receiverName, order.receiverPhone, order.receiverEmail || '',
          order.address, order.address2 || '', order.landmark, order.pincode, order.city || '', order.state || '',
          order.weight, order.length, order.width, order.height,
          order.productName, order.quantity, order.invoiceValue, order.sku || '', order.hsn || '',
          order.paymentMode, order.pickupDate || '', order.altPhone || '',
          order.addressNickname || '', order.pickupContactName || '',
          order.pickupPhone || '', order.pickupAddress || '', order.pickupAddress2 || '', order.pickupLandmark || '',
          order.pickupPincode || '', order.pickupCity || '', order.pickupState || ''
        ];
        // Escape quotes and wrap in quotes for CSV safety
        const safeRow = row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
        csvRows.push(safeRow.join(','));
      });
      
      const csvContent = csvRows.join('\n');
      const csvBlob = new Blob([csvContent], { type: 'text/csv' });
      const newFile = new File([csvBlob], file.name.replace(/\.[^/.]+$/, "") + "_processed.csv", { type: 'text/csv' });

      const warehousePayload = {}; // Empty, relying on row-level addresses

      // Build configs mapping orderId -> { courierId, serviceId, planId }
      const configsPayload: Record<string, any> = {};
      readyIndices.forEach((idx) => {
        const order = orders[idx];
        const state = processStates[order.internalId];
        if (state.selectedRate) {
          configsPayload[order.orderId] = {
            courierId: state.selectedRate.courierId,
            serviceId: state.selectedRate.serviceId,
            planId: state.selectedRate.planId || 1,
          };
        }
      });

      await bulkService.uploadProcessedBulkFile(newFile, warehousePayload, configsPayload);

      toast.success(`Successfully uploaded and shipped ${readyIndices.length} orders!`, {
        id: toastId,
      });

      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Bulk upload processing failed';
      toast.error(errorMsg, { id: toastId });
      setIsFinalizing(false);
    }
  };

  const readyCount = useMemo(() => {
    return orders.filter((o) => processStates[o.internalId]?.status === 'ready').length;
  }, [orders, processStates]);

  const totalShippingCost = useMemo(() => {
    return orders.reduce((acc, order) => {
      const state = processStates[order.internalId];
      if (state?.status === 'ready') {
        return acc + (state.selectedRate?.totalCharge || 0);
      }
      return acc;
    }, 0);
  }, [orders, processStates]);

  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex-shrink-0 bg-teal px-6 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white">Process Orders</h2>
            <p className="text-xs text-white/80">
              {orders.length} orders to process
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {selectedIndices.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={deleteSelected}
              className="bg-white/10 hover:bg-red-500 text-white border-white/20 h-10 px-4"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedIndices.length})
            </Button>
          )}

          <div className="flex bg-white/10 rounded-lg p-1 border border-white/20">
            <Tooltip text="Previous Error" side="bottom">
              <button
                onClick={() => jumpToErrorRow('prev')}
                className="p-1 hover:bg-white/20 text-white rounded transition-colors"
                disabled={orders.every((o) => o.isValid)}
              >
                <ChevronUp className="h-5 w-5" />
              </button>
            </Tooltip>
            <div className="w-px bg-white/20 mx-1" />
            <Tooltip text="Next Error" side="bottom">
              <button
                onClick={() => jumpToErrorRow('next')}
                className="p-1 hover:bg-white/20 text-white rounded transition-colors"
                disabled={orders.every((o) => o.isValid)}
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </Tooltip>
          </div>

          <div className="flex items-center gap-4">
            {readyCount > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-300  font-bold leading-none">
                  Total shipping cost
                </p>
                <p className="text-lg mt-1 font-black text-white leading-none">
                  ₹{totalShippingCost.toLocaleString('en-IN')}
                </p>
              </div>
            )}
            <Button
              onClick={handleShipNow}
              disabled={readyCount === 0 || isFinalizing}
              size="sm"
              className="bg-white text-teal hover: font-bold px-6 h-10 shadow-lg"
            >
              {isFinalizing ? (
                <Spinner className="mr-2" />
              ) : (
                <Ship className="mr-2 h-4 w-4" />
              )}
              Ship {readyCount} Orders
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50/50 p-6">
        <div className="mx-auto space-y-4">
          {/* Header Card - Standalone */}
          <div className="hidden lg:block bg-teal text-white rounded-xl px-6 py-2 shadow-md  top-0 z-20">
            <div className="grid grid-cols-[40px_50px_120px_140px_1.2fr_120px_90px_1.5fr_60px_100px_150px_40px] gap-4 items-center text-center text-sm font-black  ">
              <div className="flex justify-center">
                <Checkbox
                  checked={
                    selectedIndices.length === orders.length &&
                    orders.length > 0
                  }
                  onChange={() =>
                    setSelectedIndices(
                      selectedIndices.length === orders.length
                        ? []
                        : orders.map((_, i) => i)
                    )
                  }
                  className="border-white/40 checked:bg-white checked:text-teal"
                />
              </div>
              <div>S No.</div>
              <div className="text-left">Order Number</div>
              <div className="text-left">Pickup Date</div>
              <div className="text-left">Recipient</div>
              <div>Invoice Value</div>
              <div>Ch. Weight</div>
              <div className="text-left">Courier Selection</div>
              <div>TAT</div>
              <div className="text-right">Cost</div>
              <div className="text-left">Status</div>
              <div>Action</div>
            </div>
          </div>

          {/* Rows - Individual Cards */}
          <div className="space-y-3">
            {orders.map((order, idx) => {
              const state = processStates[order.internalId] || {
                isLoadingRates: true,
                rates: [],
                status: 'loading_rates',
              };
              const isSelected = selectedIndices.includes(idx);
              const isError = !order.isValid || state.status === 'error';

              return (
                <div
                  key={order.internalId}
                  ref={(el) => (rowRefs.current[idx] = el)}
                  className={cn(
                    'group bg-white border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 px-6 py-2 relative',
                    isSelected
                      ? 'ring-2 ring-teal ring-offset-1 border-transparent'
                      : 'border-gray-200',
                    isError ? 'bg-red-50/30' : '',
                    currentErrorIdx === idx
                      ? 'ring-2 ring-yellow-400 ring-offset-2'
                      : ''
                  )}
                >
                  <div className="grid grid-cols-[40px_50px_120px_140px_1.2fr_100px_90px_1.5fr_60px_100px_150px_40px] gap-4 items-center text-center text-xs">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={isSelected}
                        onChange={() =>
                          setSelectedIndices((prev) =>
                            isSelected
                              ? prev.filter((i) => i !== idx)
                              : [...prev, idx]
                          )
                        }
                      />
                    </div>

                    <div className="text-gray-400 font-bold tabular-nums">
                      {String(idx + 1).padStart(2, '0')}
                    </div>

                    <div className="text-left font-black text-gray-900 group-hover:text-teal transition-colors truncate max-w-[60px]" title={`#${order.orderId}`}>
                      #{order.orderId}
                    </div>

                    <div className="text-left font-medium text-gray-600">
                      {order.pickupDate || '-'}
                    </div>

                    <div className="text-left truncate">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800 text-sm">
                          {order.receiverName}
                        </span>
                        <span className="text-sm text-gray-400 font-medium">
                          {order.city}, {order.state}
                        </span>
                      </div>
                    </div>

                    <div className="font-black text-gray-700  py-1 rounded">
                      ₹{order.invoiceValue}
                    </div>

                    <div className="font-bold text-teal">
                      {(() => {
                        const vol =
                          (order.length * order.width * order.height) / 5000;
                        return Math.max(order.weight, vol).toFixed(2);
                      })()}{' '}
                      Kg
                    </div>

                    <div className="text-left">
                      {state.isLoadingRates ? (
                        <div className="flex items-center gap-2 text-gray-400 italic">
                          <Spinner size="sm" />
                        </div>
                      ) : state.rates.length > 0 ? (
                        <RateSelectDropdown
                          rates={state.rates}
                          selected={state.selectedRate}
                          onSelect={(c, s) => handleRateSelect(order.internalId, c, s)}
                        />
                      ) : (
                        <span className="text-red-400 font-semibold flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> No rates
                        </span>
                      )}
                    </div>

                    <div className="font-black text-gray-700">
                      {state.selectedRate?.tat ? (
                        <span className=" px-2 py-0.5 rounded-full text-sm">
                          {state.selectedRate.tat}d
                        </span>
                      ) : (
                        '-'
                      )}
                    </div>

                    <div className="text-right">
                      {state.selectedRate ? (
                        <span className="text-sm font-black text-teal">
                          ₹{state.selectedRate.totalCharge}
                        </span>
                      ) : (
                        '-'
                      )}
                    </div>

                    <div className="text-left">
                      {isError ? (
                        <div className="flex items-center gap-1 text-red-600 font-bold">
                          <AlertCircle className="h-3 w-3 shrink-0" />
                          <Tooltip
                            text={
                              order.errors.length > 0
                                ? order.errors
                                  .map((e) => `${e.column}: ${e.message}`)
                                  .join('\n')
                                : state.rateError || 'Error fetching rates'
                            }
                            side="left"
                          >
                            <span className="truncate max-w-[120px] cursor-help border-b border-dotted border-red-300 block">
                              {order.errors.length > 0
                                ? order.errors[0].message
                                : state.rateError || 'Rate issue'}
                            </span>
                          </Tooltip>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-green-600 font-bold">
                          <CheckCircle2 className="h-3 w-3" /> Ready
                        </div>
                      )}
                    </div>

                    <div className="flex justify-center">
                      <button
                        onClick={() => handleDeleteRow(idx)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Global Finishing States */}
      {isFinalizing && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center text-white">
          <Loader />
          <p className="mt-4 font-bold text-lg tracking-wider animate-pulse">
            PROCESSING SHIPMENTS...
          </p>
        </div>
      )}
    </div>
  );
}

function RateSelectDropdown({
  rates,
  selected,
  onSelect,
}: {
  rates: CourierRate[];
  selected: any;
  onSelect: (c: CourierRate, s: ServiceRate) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center justify-between w-full px-2 py-1 bg-white border border-gray-200 rounded hover:border-teal transition-all group min-h-[32px]">
          <div className="flex items-center gap-2 overflow-hidden">
            {selected?.courierId && (
              <img
                src={
                  rates.find((r) => r.courierId === selected.courierId)?.logoUrl
                }
                className="h-4 w-4 shrink-0 rounded-full"
              />
            )}
            <span className="truncate font-medium text-gray-700 text-sm">
              {selected
                ? `${selected.courierName} (${selected.serviceName.replace(/by\s+/i, '')})`
                : 'Select Courier'}
            </span>
          </div>
          {selected && (
            <div className="ml-auto mr-2 px-1.5 py-0.5  rounded text-sm font-bold text-gray-500 whitespace-nowrap">
              Slab:{' '}
              {
                rates
                  .find((c) => c.courierId === selected.courierId)
                  ?.services.find((s) => s.serviceId === selected.serviceId)
                  ?.baseWeightSlab
              }{' '}
              Kg
            </div>
          )}
          <ChevronDown className="h-3 w-3 text-gray-400 group-hover:text-teal shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="start"
          className="w-[480px] bg-white border rounded-lg shadow-2xl z-[100] max-h-[400px] overflow-hidden flex flex-col"
        >
          {/* Table Header */}
          <div className="flex-shrink-0  border-b px-4 py-2 grid grid-cols-[1fr_100px_80px_60px] gap-2 text-sm font-bold text-gray-500  ">
            <div>Courier & Service</div>
            <div className="text-center">Slab</div>
            <div className="text-center">TAT</div>
            <div className="text-right">Cost</div>
          </div>

          <div className="overflow-y-auto">
            {rates.flatMap((c) =>
              c.services.map((s) => ({ courier: c, service: s }))
            )
            .sort((a, b) => (a.service.totalCharge || 0) - (b.service.totalCharge || 0))
            .map(({ courier: c, service: s }) => (
                <DropdownMenuItem
                  key={`${c.courierId}-${s.serviceId}`}
                  onSelect={() => onSelect(c, s)}
                  className={cn(
                    'grid grid-cols-[1fr_100px_80px_60px] gap-2 p-2 cursor-pointer hover:bg-teal/5 border-b last:border-0 items-center transition-colors',
                    selected?.serviceId === s.serviceId
                      ? 'bg-teal/5 ring-1 ring-inset ring-teal/20'
                      : ''
                  )}
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <img
                      src={c.logoUrl}
                      className="h-7 w-7 rounded-full border p-0.5 bg-white shrink-0"
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-bold text-gray-900 truncate">
                        {c.courierName}
                      </span>
                      <span className="text-sm text-gray-400 truncate">
                        {s.displayName}
                      </span>
                    </div>
                  </div>

                  <div className="text-center">
                    <span className="text-xs font-semibold text-gray-600  px-2 py-0.5 rounded">
                      {s.baseWeightSlab} Kg
                    </span>
                  </div>

                  <div className="text-center">
                    <span className="text-xs font-medium text-gray-600">
                      {s.tat} Days
                    </span>
                  </div>

                  <div className="text-right">
                    <span className="text-sm font-black text-teal">
                      ₹{s.totalCharge}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))
            }
          </div>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
